import { z } from "zod";
import { EUserTypeV4 } from "../constants";
import { paginationMetaSchema } from "../interfaces/base-api-response";
import { productSingleVariantResponseSchema } from "../interfaces/schemas/product-v4.schemas";
import { paginationSchema } from "../interfaces/pagination";
import { DiscountValueType } from "../interfaces/discount";

export enum OrderEditRequestStatus {
  PENDING = "PENDING",
  ACCEPTED = "ACCEPTED",
  REJECTED = "REJECTED",
  EXPIRED = "EXPIRED",
}

const editRequestLineItemSchema = z.object({
  productId: z.string(),
  variantId: z.string(),
  quantity: z.number(),
  price: z.number().nullish(),
  metadata: z.record(z.string(), z.unknown()).nullish(),
});

export const editRequestItemChangeSchema = z.object({
  product: productSingleVariantResponseSchema,
  quantityDelta: z.number().int(),
});

/**
 * The coupon applied to the order, with what it is worth on each side of the revision. A percentage
 * coupon re-prices with the subtotal (`revisedAmount` differs); a flat one does not.
 */
export const editRequestDiscountSchema = z.object({
  code: z.string(),
  valueType: z.nativeEnum(DiscountValueType),
  value: z.number(),
  originalAmount: z.number(),
  revisedAmount: z.number(),
});

/**
 * The discount/coins-aware money fields the backend added alongside coupon-on-edit support. They are
 * declared optional on the wire because deployments that predate that change omit them; this fills
 * them in so every consumer sees plain numbers. Drop the fallbacks once the API always sends them.
 */
export const editRequestPayableMoneyShape = {
  originalPayableAmount: z.number().optional(),
  revisedPayableAmount: z.number().optional(),
  payableDeltaAmount: z.number().optional(),
  coinsDebitAmount: z.number().optional(),
};

interface IEditRequestPayableMoneyInput {
  originalAmount: number;
  revisedAmount: number;
  originalPayableAmount?: number;
  revisedPayableAmount?: number;
  payableDeltaAmount?: number;
  coinsDebitAmount?: number;
  discount?: { originalAmount: number; revisedAmount: number } | null;
}

export function deriveEditRequestPayableMoney(money: IEditRequestPayableMoneyInput) {
  const coinsDebitAmount = money.coinsDebitAmount ?? 0;
  const originalPayableAmount =
    money.originalPayableAmount ??
    Math.max(money.originalAmount - (money.discount?.originalAmount ?? 0), 0);
  const revisedPayableAmount =
    money.revisedPayableAmount ??
    Math.max(money.revisedAmount - (money.discount?.revisedAmount ?? 0) - coinsDebitAmount, 0);

  return {
    coinsDebitAmount,
    originalPayableAmount,
    revisedPayableAmount,
    payableDeltaAmount: money.payableDeltaAmount ?? revisedPayableAmount - originalPayableAmount,
  };
}

const orderEditRequestResponseBaseSchema = z.object({
  id: z.string(),
  orderId: z.string(),
  sessionId: z.string(),
  createdBy: z.string(),
  initiatorType: z.nativeEnum(EUserTypeV4),
  originalItems: z.array(editRequestLineItemSchema),
  revisedItems: z.array(editRequestLineItemSchema),
  /** Pre-discount item subtotals. Never show these as "order total" — see the payable fields. */
  originalAmount: z.number(),
  revisedAmount: z.number(),
  deltaAmount: z.number(),
  /** Net of discount and coins: what the order was, and becomes, payable at. */
  ...editRequestPayableMoneyShape,
  discount: editRequestDiscountSchema.nullish(),
  status: z.nativeEnum(OrderEditRequestStatus),
  expiresAt: z.number(),
  resolvedAt: z.number().nullish(),
  resolvedBy: z.string().nullish(),
  resolvedByType: z.nativeEnum(EUserTypeV4).nullish(),
  partnerNote: z.string().nullish(),
  orderUpdated: z.boolean(),
  createdAt: z.number(),
  updatedAt: z.number(),
  paidAmount: z.number(),
  /** Signed: positive is due from the customer, negative is a refund owed to them. */
  balanceToPay: z.number(),
  itemChanges: z.array(editRequestItemChangeSchema),
});

export const orderEditRequestResponseSchema = orderEditRequestResponseBaseSchema.transform(
  (editRequest) => ({
    ...editRequest,
    ...deriveEditRequestPayableMoney(editRequest),
  }),
);

export const orderEditRequestListResponseSchema = z.object({
  items: z.array(orderEditRequestResponseSchema),
  meta: paginationMetaSchema,
});

export const searchAdminEditRequestsRequestSchema = paginationSchema.extend({
  status: z.preprocess(
    (val) => (Array.isArray(val) ? val : val != null ? [val] : undefined),
    z.array(z.nativeEnum(OrderEditRequestStatus)).optional(),
  ),
  sessionId: z.string().uuid().nullish(),
  orderId: z.string().uuid().nullish(),
  userId: z.string().nullish(),
  limit: z.coerce.number().int().min(1).max(50).default(10),
});

export type OrderEditRequestItemChangeResponseDto = z.infer<
  typeof editRequestItemChangeSchema
>;
export type EditRequestDiscountDto = z.infer<typeof editRequestDiscountSchema>;
export type OrderEditRequestResponseDto = z.infer<
  typeof orderEditRequestResponseSchema
>;
export type OrderEditRequestListResponseDto = z.infer<
  typeof orderEditRequestListResponseSchema
>;
export type SearchAdminEditRequestsRequestDto = z.infer<
  typeof searchAdminEditRequestsRequestSchema
>;

export const resolveEditRequestRequestSchema = z.object({
  accept: z.boolean(),
});

export type ResolveEditRequestRequestDto = z.infer<
  typeof resolveEditRequestRequestSchema
>;

export const createEditRequestItemSchema = z.object({
  productId: z.string().uuid(),
  variantId: z.string().uuid(),
  quantity: z.number().int().min(1),
  price: z.number().nonnegative("Price must be a non-negative number"),
  metadata: z.record(z.string(), z.unknown()).nullish(),
});

export const adminCreateEditRequestRequestSchema = z.object({
  orderId: z.string().uuid(),
  sessionId: z.string().uuid(),
  revisedItems: z.array(createEditRequestItemSchema).min(1),
  partnerNote: z.string().max(500).optional(),
});

export type AdminCreateEditRequestRequestDto = z.infer<
  typeof adminCreateEditRequestRequestSchema
>;
